import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);
  const location = useLocation();

  // Robust role detection
  // 1. Check localStorage
  // 2. Check if the path is specifically linked from the candidate side
  // 3. Default to candidate if no admin markers are present
  const userRole = localStorage.getItem('userRole');
  const isCandidate = userRole === 'candidate' || 
                     (!userRole && location.pathname === '/profile' && !location.search.includes('admin'));

  const candidateData = {
    name: 'Yaw Mensah',
    email: 'yaw.m@example.com',
    role: 'Candidate',
    phone: '+233 55 987 6543',
    location: 'Kumasi, Ghana',
    bio: 'Seasoned operations professional with a proven track record of optimizing supply chain workflows and leading large cross-functional teams. Seeking to leverage my 8 years of experience in the logistics sector.',
    avatar: 'https://images.unsplash.com/photo-1520341280432-4749d4d7bcf9?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80'
  };

  const adminData = {
    name: 'Admin User',
    email: 'admin@rmghana.com',
    role: 'HR Manager',
    phone: '+233 24 123 4567',
    location: 'Accra, Ghana',
    bio: 'Experienced HR professional specializing in talent acquisition and organizational development within the West African market.',
    avatar: 'https://i.pravatar.cc/150?img=11'
  };

  // Initialize state based on detected role
  const [profile, setProfile] = useState(isCandidate ? candidateData : adminData);

  // Sync profile if role changes (e.g., during testing/demo)
  useEffect(() => {
    setProfile(isCandidate ? candidateData : adminData);
  }, [isCandidate]);

  const handleSave = (e) => {
    e.preventDefault();
    setIsEditing(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfile({ ...profile, avatar: URL.createObjectURL(file) });
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-10 py-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
        <button 
          onClick={() => setIsEditing(!isEditing)}
          className="flex items-center gap-2 text-gray-600 hover:text-[#11469A] transition-colors font-medium text-sm"
        >
          <SafeIcon name={isEditing ? "X" : "Edit2"} size={16} />
          {isEditing ? "Cancel" : "Edit Profile"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Column - Profile Summary & Links */}
        <div className="lg:col-span-4 space-y-8">
          <div className="space-y-4">
            <div className="relative inline-block">
              <img 
                src={profile.avatar} 
                alt="Profile" 
                className="w-32 h-32 rounded-2xl object-cover shadow-sm border border-gray-100" 
              />
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-3 -right-3 p-2.5 bg-[#00AEEF] text-white rounded-xl shadow-md hover:bg-[#009bd6] transition-all"
              >
                <SafeIcon name="Camera" size={16} />
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleImageUpload} 
                className="hidden" 
                accept="image/*" 
              />
            </div>
            <div className="space-y-1 pt-2 text-left">
              <h2 className="text-2xl font-bold text-gray-800">{profile.name}</h2>
              <p className="text-xs font-bold text-[#11469A] uppercase tracking-widest">{profile.role}</p>
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-widest text-left">Account Links</h3>
            <div className="space-y-1">
              <button className="w-full flex items-center justify-between p-3 text-sm text-gray-600 hover:bg-white hover:shadow-sm rounded-xl transition-all border border-transparent">
                <div className="flex items-center gap-3">
                  <SafeIcon name="Shield" size={18} className="text-gray-400" />
                  <span className="font-medium">Security</span>
                </div>
                <SafeIcon name="ChevronRight" size={16} className="text-gray-400" />
              </button>
              <button className="w-full flex items-center justify-between p-3 text-sm text-gray-600 hover:bg-white hover:shadow-sm rounded-xl transition-all border border-transparent">
                <div className="flex items-center gap-3">
                  <SafeIcon name="Bell" size={18} className="text-gray-400" />
                  <span className="font-medium">Notifications</span>
                </div>
                <SafeIcon name="ChevronRight" size={16} className="text-gray-400" />
              </button>
              {isCandidate && (
                <button className="w-full flex items-center justify-between p-3 text-sm text-gray-600 hover:bg-white hover:shadow-sm rounded-xl transition-all border border-transparent">
                  <div className="flex items-center gap-3">
                    <SafeIcon name="FileText" size={18} className="text-gray-400" />
                    <span className="font-medium">My Resumes</span>
                  </div>
                  <SafeIcon name="ChevronRight" size={16} className="text-gray-400" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Form Details */}
        <div className="lg:col-span-8">
          <form onSubmit={handleSave} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8 text-left">
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Full Name</label>
                <input 
                  type="text" 
                  disabled={!isEditing}
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full px-4 h-11 rounded-lg border border-gray-200 bg-white text-gray-700 focus:border-[#11469A] outline-none transition-all disabled:bg-gray-50/50 disabled:text-gray-500 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Email Address</label>
                <input 
                  type="email" 
                  disabled={!isEditing}
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="w-full px-4 h-11 rounded-lg border border-gray-200 bg-white text-gray-700 focus:border-[#11469A] outline-none transition-all disabled:bg-gray-50/50 disabled:text-gray-500 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Phone Number</label>
                <input 
                  type="text" 
                  disabled={!isEditing}
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full px-4 h-11 rounded-lg border border-gray-200 bg-white text-gray-700 focus:border-[#11469A] outline-none transition-all disabled:bg-gray-50/50 disabled:text-gray-500 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Location</label>
                <input 
                  type="text" 
                  disabled={!isEditing}
                  value={profile.location}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  className="w-full px-4 h-11 rounded-lg border border-gray-200 bg-white text-gray-700 focus:border-[#11469A] outline-none transition-all disabled:bg-gray-50/50 disabled:text-gray-500 text-sm"
                />
              </div>
            </div>

            <div className="space-y-2 text-left">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Professional Bio</label>
              <textarea 
                disabled={!isEditing}
                value={profile.bio}
                rows={5}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                className="w-full p-4 rounded-lg border border-gray-200 bg-white text-gray-600 focus:border-[#11469A] outline-none transition-all disabled:bg-white disabled:border-gray-200 resize-none text-sm leading-relaxed"
              />
            </div>

            {isEditing && (
              <div className="pt-4 text-left">
                <button 
                  type="submit"
                  className="bg-[#11469A] hover:bg-[#0d3678] text-white px-8 h-11 rounded-lg text-sm font-bold shadow-md transition-all uppercase tracking-wider"
                >
                  Save Changes
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;